import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { 
  Stethoscope, 
  Send, 
  Image as ImageIcon, 
  FileText, 
  Upload,
  X,
  Loader2,
  ArrowLeft,
  Users,
  TestTube,
  Scan,
  Bot,
  User,
  AlertTriangle,
  AlertCircle,
  ShieldAlert,
  Activity,
  FileUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Severity level configurations
const SEVERITY_CONFIG = {
  1: { name: "Minor", color: "bg-emerald-500", textColor: "text-emerald-400", icon: Activity, description: "Non-urgent, home care" },
  2: { name: "Moderate", color: "bg-amber-500", textColor: "text-amber-400", icon: AlertCircle, description: "See doctor in 24-48h" },
  3: { name: "Significant", color: "bg-orange-500", textColor: "text-orange-400", icon: AlertTriangle, description: "See doctor within 24h" },
  4: { name: "Severe", color: "bg-red-500", textColor: "text-red-400", icon: ShieldAlert, description: "Immediate consultation" },
  5: { name: "Critical", color: "bg-red-600", textColor: "text-red-500", icon: ShieldAlert, description: "EMERGENCY" }
};

const DiagnosisChat = () => {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [currentSeverity, setCurrentSeverity] = useState(null);
  const [isInvestigationBlocked, setIsInvestigationBlocked] = useState(false);
  const [provisionalDiagnosis, setProvisionalDiagnosis] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const reportInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    createSession();
  }, []);

  const createSession = async () => {
    try {
      setIsCreatingSession(true);
      const response = await axios.post(`${API}/chat/session`, {
        patient_name: null
      }, { withCredentials: true });
      setSessionId(response.data.id);
      
      setMessages([{
        id: "welcome",
        role: "assistant",
        content: `Hello! I'm **Dr. Veena** from MedPlexity.

Tell me your symptoms and I'll give you a **direct assessment** with:
- Preliminary diagnosis
- Immediate advice
- Next steps

**Example:** "I have fever and body pain for 2 days"

📷 You can also upload images or 📄 lab reports for analysis.

*What's troubling you today?*`,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error("Error creating session:", error);
      toast.error("Failed to start diagnosis session. Please refresh.");
    } finally {
      setIsCreatingSession(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() && !selectedImage) return;
    if (!sessionId) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: inputMessage,
      timestamp: new Date().toISOString(),
      has_image: !!selectedImage
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    const loadingId = `loading-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: loadingId,
      role: "assistant",
      content: "loading",
      timestamp: new Date().toISOString()
    }]);

    try {
      let imageBase64 = null;
      if (selectedImage) {
        const reader = new FileReader();
        imageBase64 = await new Promise((resolve) => {
          reader.onload = (e) => {
            const base64 = e.target.result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(selectedImage);
        });
      }

      const response = await axios.post(`${API}/chat/diagnosis-message`, {
        session_id: sessionId,
        message: inputMessage || "Please analyze this image for clinical signs.",
        image_base64: imageBase64
      }, { withCredentials: true });

      // Update state based on response
      if (response.data.severity_level) {
        setCurrentSeverity(response.data.severity_level);
      }
      if (response.data.investigation_blocked) {
        setIsInvestigationBlocked(true);
      }
      if (response.data.provisional_diagnosis) {
        setProvisionalDiagnosis(response.data.provisional_diagnosis);
      }

      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== loadingId);
        return [...filtered, {
          id: response.data.id,
          role: "assistant",
          content: response.data.content,
          timestamp: response.data.timestamp,
          severity_level: response.data.severity_level,
          investigation_blocked: response.data.investigation_blocked
        }];
      });

      setSelectedImage(null);
      setImagePreview(null);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => prev.filter(m => m.id !== loadingId));
      toast.error("Failed to get response. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        toast.error("Please upload JPEG, PNG, or WEBP images only.");
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
      setShowUploadOptions(false);
    }
  };

  const handleReportUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please upload PDF, JPEG, PNG, or WEBP files only.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      formData.append("file", file);

      toast.info("Uploading and analyzing report...");
      const uploadResponse = await axios.post(`${API}/upload/report`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true
      });

      const extractedData = uploadResponse.data.extracted_data;
      
      // Show success with extraction info
      if (extractedData && !extractedData.error) {
        toast.success("Report analyzed successfully!");
        
        // Build summary of extracted data
        let reportSummary = `Uploaded ${file.name}. `;
        if (extractedData.report_type) {
          reportSummary += `Report Type: ${extractedData.report_type}. `;
        }
        if (extractedData.abnormal_findings?.length > 0) {
          reportSummary += `Abnormal findings: ${extractedData.abnormal_findings.join(", ")}. `;
        }
        if (extractedData.summary) {
          reportSummary += extractedData.summary;
        }
        
        // Add extracted data as a system message
        if (extractedData.values) {
          const abnormalValues = [];
          for (const [key, val] of Object.entries(extractedData.values)) {
            if (val.value && val.status && val.status !== "normal") {
              abnormalValues.push(`${key}: ${val.value} ${val.unit} (${val.status})`);
            }
          }
          
          if (abnormalValues.length > 0) {
            setMessages(prev => [...prev, {
              id: `report-${Date.now()}`,
              role: "system",
              content: `📋 **Report Analysis Complete**\n\nExtracted clinical values:\n${abnormalValues.map(v => `• ${v}`).join('\n')}\n\n${extractedData.summary || ''}`,
              timestamp: new Date().toISOString(),
              isReportData: true
            }]);
          }
        }
        
        setShowUploadOptions(false);

        // If blocked, continue diagnosis with extracted data
        if (isInvestigationBlocked) {
          setIsInvestigationBlocked(false);
          const response = await axios.post(`${API}/chat/continue-after-report/${sessionId}`, {
            report_summary: reportSummary
          });
          
          if (response.data.severity_level) {
            setCurrentSeverity(response.data.severity_level);
          }
          if (response.data.provisional_diagnosis) {
            setProvisionalDiagnosis(response.data.provisional_diagnosis);
          }
          
          setMessages(prev => [...prev, {
            id: response.data.id,
            role: "assistant",
            content: response.data.content,
            timestamp: response.data.timestamp,
            severity_level: response.data.severity_level
          }]);
        }
      } else {
        toast.success("Report uploaded! Analysis pending.");
        setShowUploadOptions(false);
      }
    } catch (error) {
      console.error("Error uploading report:", error);
      toast.error("Failed to upload report.");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleViewDiseaseProfile = () => {
    if (provisionalDiagnosis) {
      navigate(`/disease/${encodeURIComponent(provisionalDiagnosis)}`);
    }
  };

  const renderSeverityBadge = (level) => {
    if (!level) return null;
    const config = SEVERITY_CONFIG[level];
    const Icon = config.icon;
    
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.color}/20 border border-current ${config.textColor} text-sm font-medium`}>
        <Icon className="w-4 h-4" />
        <span>{config.name}</span>
        <span className="text-xs opacity-75">- {config.description}</span>
      </div>
    );
  };

  const renderMessageContent = (content, severityLevel, investigationBlocked) => {
    if (content === "loading") {
      return (
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-[#00FF94]" />
          <span className="text-[#00FF94] neon-text">Dr. Veena is analyzing your symptoms...</span>
        </div>
      );
    }

    // Clean up special markers for display
    let displayContent = content
      .replace(/\[SEVERITY:\d\]/g, '')
      .replace(/\[INVESTIGATION_BLOCKER\]/g, '')
      .replace(/\[\/INVESTIGATION_BLOCKER\]/g, '')
      .replace(/\[PROVISIONAL_DIAGNOSIS\]/g, '')
      .replace(/\[\/PROVISIONAL_DIAGNOSIS\]/g, '')
      .replace(/\[TREATMENT_PROTOCOL\]/g, '')
      .replace(/\[\/TREATMENT_PROTOCOL\]/g, '');

    const lines = displayContent.split('\n');
    
    return (
      <div className="space-y-3">
        {severityLevel && (
          <div className="mb-4">
            {renderSeverityBadge(severityLevel)}
          </div>
        )}
        
        {investigationBlocked && (
          <div className="mb-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center gap-2 text-amber-400 font-semibold mb-2">
              <AlertTriangle className="w-5 h-5" />
              Investigation Required
            </div>
            <p className="text-amber-200 text-sm">
              Please upload the requested reports to continue with the diagnosis.
            </p>
          </div>
        )}
        
        <div className="markdown-content space-y-2">
          {lines.map((line, idx) => {
            if (line.startsWith('### ')) {
              return <h3 key={idx} className="text-lg font-semibold text-[#00FF94] mt-3">{line.slice(4)}</h3>;
            }
            if (line.startsWith('## ')) {
              return <h2 key={idx} className="text-xl font-semibold text-[#00FF94] mt-4">{line.slice(3)}</h2>;
            }
            if (line.startsWith('# ')) {
              return <h1 key={idx} className="text-2xl font-bold text-[#00FF94] mt-4">{line.slice(2)}</h1>;
            }
            let processedLine = line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#00FF94]">$1</strong>');
            if (line.startsWith('- ') || line.startsWith('* ')) {
              return <li key={idx} className="ml-4 list-disc" dangerouslySetInnerHTML={{ __html: processedLine.slice(2) }} />;
            }
            if (/^\d+\.\s/.test(line)) {
              return <li key={idx} className="ml-4 list-decimal" dangerouslySetInnerHTML={{ __html: processedLine.replace(/^\d+\.\s/, '') }} />;
            }
            if (!line.trim()) {
              return <div key={idx} className="h-2" />;
            }
            return <p key={idx} dangerouslySetInnerHTML={{ __html: processedLine }} />;
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-[#0A0A0A]/90 backdrop-blur-xl sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Button 
            data-testid="back-btn"
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-slate-400 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#00FF94]/20 flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-[#00FF94]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold font-['Manrope']">Dr. Veena</span>
                <div className="status-dot" />
              </div>
              <span className="text-slate-500 text-xs">MedPlexity AI Diagnosis</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {currentSeverity && (
            <Badge 
              className={`${SEVERITY_CONFIG[currentSeverity].color} text-white`}
              data-testid="severity-badge"
            >
              {SEVERITY_CONFIG[currentSeverity].name}
            </Badge>
          )}
          
          {provisionalDiagnosis && (
            <Button 
              data-testid="view-profile-btn"
              onClick={handleViewDiseaseProfile}
              variant="outline"
              size="sm"
              className="border-[#00FF94]/50 text-[#00FF94] hover:bg-[#00FF94]/10"
            >
              <Activity className="w-4 h-4 mr-2" />
              Disease Profile
            </Button>
          )}
          
          <Button 
            data-testid="consult-doctor-btn"
            onClick={() => navigate(provisionalDiagnosis ? `/consult-doctor?diagnosis=${encodeURIComponent(provisionalDiagnosis)}` : "/consult-doctor")}
            variant="outline"
            className="border-[#00FF94]/50 text-[#00FF94] hover:bg-[#00FF94]/10"
          >
            <Users className="w-4 h-4 mr-2" />
            Consult Doctor
          </Button>
        </div>
      </header>

      {/* Severity Alert Banner */}
      {currentSeverity && currentSeverity >= 4 && (
        <div className={`px-4 py-3 ${currentSeverity === 5 ? 'bg-red-600' : 'bg-red-500'} text-white flex items-center justify-center gap-3`}>
          <ShieldAlert className="w-5 h-5" />
          <span className="font-semibold">
            {currentSeverity === 5 
              ? "CRITICAL: Please seek emergency medical care immediately!" 
              : "SEVERE: Immediate medical consultation recommended"}
          </span>
          <Button 
            size="sm"
            variant="secondary"
            onClick={() => navigate("/consult-doctor")}
            className="ml-4"
          >
            Find Doctor Now
          </Button>
        </div>
      )}

      {/* Chat Area */}
      <div className="flex-1 flex flex-col max-w-5xl mx-auto w-full">
        <ScrollArea className="flex-1 p-4 md:p-6 chat-scroll" data-testid="chat-messages-area">
          <div className="space-y-6">
            {messages.map((message) => (
              <div 
                key={message.id}
                className={`flex gap-3 ${message.role === "user" ? "justify-end" : message.role === "system" ? "justify-center" : "justify-start"} animate-fadeInUp`}
              >
                {message.role === "system" ? (
                  // System message for report data
                  <div className="max-w-[90%] rounded-xl px-5 py-4 bg-blue-500/10 border border-blue-500/30 text-blue-200">
                    {renderMessageContent(message.content, null, false)}
                  </div>
                ) : (
                  <>
                    {message.role === "assistant" && (
                      <div className="w-9 h-9 rounded-full bg-[#00FF94]/20 flex items-center justify-center flex-shrink-0 mt-1">
                        <Bot className="w-5 h-5 text-[#00FF94]" />
                      </div>
                    )}
                    <div 
                      className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-5 py-4 ${
                        message.role === "user" 
                          ? "message-user text-white" 
                          : "message-ai text-slate-200"
                      }`}
                      data-testid={`message-${message.role}`}
                    >
                      {message.has_image && (
                        <div className="mb-2 text-xs text-slate-400 flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" /> Image attached
                        </div>
                      )}
                      {renderMessageContent(message.content, message.severity_level, message.investigation_blocked)}
                    </div>
                    {message.role === "user" && (
                      <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0 mt-1">
                        <User className="w-5 h-5 text-slate-300" />
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Investigation Blocker Upload Banner */}
        {isInvestigationBlocked && (
          <div className="px-4 py-4 border-t border-amber-500/30 bg-amber-500/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
                <div>
                  <p className="text-amber-400 font-semibold">Investigation Required</p>
                  <p className="text-amber-200/70 text-sm">Upload the requested reports to continue diagnosis</p>
                </div>
              </div>
              <Button 
                onClick={() => reportInputRef.current?.click()}
                className="bg-amber-500 text-black hover:bg-amber-400"
                data-testid="upload-report-blocker-btn"
              >
                <FileUp className="w-4 h-4 mr-2" />
                Upload Report
              </Button>
            </div>
          </div>
        )}

        {/* External Lab Test Links */}
        <div className="px-4 md:px-6 py-3 border-t border-white/5 flex items-center gap-3 overflow-x-auto">
          <span className="text-slate-500 text-sm whitespace-nowrap">Get tested:</span>
          <a 
            href="https://www.healthians.com/blood-test/gurgaon"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-sm hover:bg-blue-500/20 transition-colors whitespace-nowrap"
            data-testid="blood-test-link"
          >
            <TestTube className="w-4 h-4" />
            Blood Tests
          </a>
          <a 
            href="https://www.healthians.com/scans"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-400 text-sm hover:bg-purple-500/20 transition-colors whitespace-nowrap"
            data-testid="scans-link"
          >
            <Scan className="w-4 h-4" />
            Scans
          </a>
        </div>

        {/* Image Preview */}
        {imagePreview && (
          <div className="px-4 md:px-6 py-3 border-t border-white/5">
            <div className="relative inline-block">
              <img 
                src={imagePreview} 
                alt="Selected" 
                className="h-20 rounded-lg border border-[#00FF94]/30"
              />
              <button 
                onClick={() => { setSelectedImage(null); setImagePreview(null); }}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center"
                data-testid="remove-image-btn"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="px-4 md:px-6 py-4 border-t border-white/5 bg-[#0A0A0A]/90 backdrop-blur-xl">
          <div className="flex items-end gap-3">
            <div className="relative">
              <Button 
                data-testid="upload-btn"
                variant="ghost"
                size="icon"
                onClick={() => setShowUploadOptions(!showUploadOptions)}
                className="text-slate-400 hover:text-[#00FF94] hover:bg-[#00FF94]/10"
                disabled={isLoading}
              >
                <Upload className="w-5 h-5" />
              </Button>
              
              {showUploadOptions && (
                <div className="absolute bottom-full left-0 mb-2 bg-[#0F1115] border border-white/10 rounded-xl p-2 shadow-xl min-w-[160px]">
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-white/5 text-slate-300 text-sm"
                    data-testid="upload-image-option"
                  >
                    <ImageIcon className="w-4 h-4 text-[#00FF94]" />
                    Upload Image
                  </button>
                  <button 
                    onClick={() => reportInputRef.current?.click()}
                    className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-white/5 text-slate-300 text-sm"
                    data-testid="upload-report-option"
                  >
                    <FileText className="w-4 h-4 text-blue-400" />
                    Upload Report
                  </button>
                </div>
              )}
            </div>
            
            <input 
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageSelect}
              className="hidden"
              data-testid="file-input"
            />
            
            <input 
              ref={reportInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={handleReportUpload}
              className="hidden"
              data-testid="report-input"
            />

            <div className="flex-1 relative">
              <Input
                data-testid="message-input"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isInvestigationBlocked ? "Upload reports to continue..." : "Describe your symptoms..."}
                disabled={isLoading || isCreatingSession}
                className="bg-black/50 border-white/10 focus:border-[#00FF94] focus:ring-1 focus:ring-[#00FF94] pr-12 py-6 rounded-xl text-white placeholder:text-slate-600"
              />
            </div>

            <Button 
              data-testid="send-btn"
              onClick={handleSendMessage}
              disabled={isLoading || isCreatingSession || (!inputMessage.trim() && !selectedImage)}
              className="bg-[#00FF94] text-black hover:bg-[#00CC76] shadow-[0_0_15px_rgba(0,255,148,0.3)] h-12 w-12 rounded-xl"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          
          <p className="text-slate-600 text-xs mt-3 text-center">
            ⚠️ This is diagnosis support only. Please consult a healthcare professional for final diagnosis.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DiagnosisChat;
